import { tokenAmountToUsd, usdToTokenAmount } from '@funkit/connect-core'
import { useCallback, useState } from 'react'

/**
 * A trailing point is mid-entry, not a different amount — and `dnum` throws on
 * it, which the converters answer with `'0'`. Every figure crossing between the
 * two denominations goes through here first.
 */
function withoutTrailingDot(value: string): string {
  return value.endsWith('.') ? value.slice(0, -1) : value
}

/**
 * What the withdrawal is worth in dollars, or `undefined` when nothing prices
 * it. Typed dollars are authoritative: the token amount they bought was
 * rounded down to fit the token, so reading it back reports $9.99 for a $10
 * that was typed — and the readers of this label sit on later screens, where
 * that cent has no visible explanation.
 */
function usdValueOf({
  amount,
  usdInput,
  inUsd,
  sourceUsdPrice,
  canPriceInUsd,
}: {
  amount: string
  usdInput: string
  inUsd: boolean
  sourceUsdPrice: number
  canPriceInUsd: boolean
}): string | undefined {
  if (!canPriceInUsd) {
    return undefined
  }
  if (inUsd) {
    return withoutTrailingDot(usdInput) || '0'
  }
  const typed = withoutTrailingDot(amount)
  return tokenAmountToUsd(typed === '' ? '0' : typed, sourceUsdPrice)
}

/**
 * The amount as the user types it, and which denomination they are typing in.
 *
 * - `amount` never changes meaning: the quote, the validation and the signature
 *   are all in source-token units, so when typing in dollars the typed figure
 *   is held here and `amount` is what it converts to.
 * - Held with the form rather than in the screen, which unmounts on the way to
 *   the recipient — local state would drop both on Continue.
 */
export function useWithdrawalAmountInput({
  amount,
  setAmount,
  sourceUsdPrice,
  sourceDecimals,
}: {
  /** The withdrawal amount, in source-token units. */
  amount: string
  setAmount: (amount: string) => void
  /** Spot USD price of the source token; `0` is the price hook's "unknown". */
  sourceUsdPrice: number
  /** Fraction digits the source token carries, once they're known. */
  sourceDecimals: number | undefined
}) {
  const [usdInput, setUsdInput] = useState('')
  // Both halves are required: converting before the token's precision is known
  // mints an amount finer than the token holds, which the numpad then refuses
  // every further digit for.
  const canPriceInUsd = sourceUsdPrice > 0 && sourceDecimals !== undefined
  // The standing preference; a rate that arrives late (or never) is what
  // actually decides it.
  const [prefersUsd, setPrefersUsd] = useState(true)
  const inUsd = prefersUsd && canPriceInUsd

  // Re-derived when the amount moves underneath it or a rate first arrives —
  // never every render (that would rewrite `12.` as `12`) and never on a
  // refreshed rate (that would rewrite the figure being edited).
  const [derivedFrom, setDerivedFrom] = useState({
    amount: '',
    isPriced: false,
  })
  if (derivedFrom.amount !== amount || derivedFrom.isPriced !== canPriceInUsd) {
    if (!derivedFrom.isPriced && canPriceInUsd && amount !== '') {
      setPrefersUsd(false)
    }
    setDerivedFrom({ amount, isPriced: canPriceInUsd })
    setUsdInput(
      amount === '' || !canPriceInUsd
        ? ''
        : tokenAmountToUsd(withoutTrailingDot(amount), sourceUsdPrice),
    )
  }

  const setInput = useCallback(
    (next: string) => {
      if (!inUsd) {
        setAmount(next)
        return
      }
      // `''` has to stay nothing rather than become `'0'`, which reads as a
      // typed zero and fails the minimum.
      const typed = withoutTrailingDot(next)
      const asTokens =
        typed === ''
          ? ''
          : usdToTokenAmount(typed, sourceUsdPrice, sourceDecimals)
      // Recorded as already derived, so the typed dollars survive a token
      // amount that rounding may have shortened.
      setUsdInput(next)
      setDerivedFrom({ amount: asTokens, isPriced: canPriceInUsd })
      setAmount(asTokens)
    },
    [inUsd, setAmount, sourceUsdPrice, sourceDecimals],
  )

  return {
    /** What to show and edit: dollars when {@link inUsd}, else token units. */
    input: inUsd ? usdInput : amount,
    setInput,
    inUsd,
    /**
     * Switches denomination, converting the amount rather than clearing it.
     *
     * - Deliberately does not re-price against a rate that moved since the
     *   figure was typed: rewriting what someone entered, under them, is worse
     *   than showing it at the rate they entered it at.
     * - The staleness is bounded by refreshing the price on the way into this
     *   screen rather than while it is open.
     */
    swapDenomination: useCallback(() => setPrefersUsd((was) => !was), []),
    /** Offering the swap without a rate would promise one we don't have. */
    canSwapDenomination: canPriceInUsd,
    /** What the amount is worth, or `undefined` when nothing prices it. */
    amountUsd: usdValueOf({
      amount,
      usdInput,
      inUsd,
      sourceUsdPrice,
      canPriceInUsd,
    }),
  }
}
